import { Injectable } from '@angular/core';
import {
  PageSize,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationWithOrder,
  ReportSectionConfigurationWithOrderImpl,
  ReportSectionNestedDataSection,
  ReportTemplate,
  ReportType,
  serializeReportSections
} from '@capital-access/reporting/common';
import { WeekPriceVolumeConfiguration } from './52-week-price-volume-configuration';
import { ActivitiesConfiguration } from './activities-configuration';
import { ActivityHistoryConfiguration } from './activity-history-configuration';
import { AiSummaryCardConfiguration } from './ai-summary-card-configuration';
import { BnyManagementReportHeaderConfiguration } from './bny-management-header-configuration';
import { CombinedOwnershipConfiguration } from './combined-ownership-configuration';
import { CommentaryConfiguration } from './commentary-configuration';
import { ContactBiographyConfiguration } from './contact-biography-configuration';
import { ContactHeaderConfiguration } from './contact-header-configuration';
import { CrossBorderBrokerConfiguration } from './cross-border-broker-configuration';
import { CrossBorderTradingConfiguration } from './cross-border-trading-configuration';
import { CustomFieldsConfiguration } from './custom-fields-configuration';
import { DepositaryReceiptsContactConfiguration } from './depositary-receipts-contact-configuration';
import { DisclosureConfiguration } from './disclosure-configuration';
import { ActivityIndicatorsConfiguration } from './dr-activity-indicators-configuration';
import { DRIssueAndCancelActivityConfiguration } from './dr-issue-and-cancel-activity-configuration';
import { DRPriceVolumeConfiguration } from './dr-price-volume-configuration';
import { EquityPortfolioConfiguration } from './equity-portfolio-configuration';
import { FundsSummaryConfiguration } from './funds-summary-configuration';
import { HighTradingVolumeBrokersConfiguration } from './high-trading-volume-brokers-configuration';
import { HistoricalOwnershipConfiguration } from './historical-ownership-configuration';
import { InstitutionDetailsConfiguration } from './institution-details-configuration';
import { InstitutionHeaderConfiguration } from './institution-header-configuration';
import { InvestmentApproachConfiguration } from './investment-approach-configuration';
import { ItineraryActivityAttributesConfiguration } from './itinerary-activity-attributes-configuration';
import { ItineraryActivityHeaderConfiguration } from './itinerary-activity-header-configuration';
import { ItineraryActivityHistoryConfiguration } from './itinerary-activity-history-configuration';
import { ItineraryCommentsConfiguration } from './itinerary-comments-configuration';
import { ItineraryParticipantBioConfiguration } from './itinerary-participant-bio-configuration';
import { ItineraryParticipantInfoConfiguration } from './itinerary-participant-info-configuration';
import { ItineraryPeerOwnershipConfiguration } from './itinerary-peer-ownership-configuration';
import { MonthlyDrActivityConfiguration } from './monthly-dr-activity-configuration';
import { NotesConfiguration } from './notes-configuration';
import { OwnershipConfiguration } from './ownership-configuration';
import { PeerAndIndustryOwnershipConfiguration } from './peer-industry-ownership-configuration';
import { PriceActivityConfiguration } from './price-activity-configuration';
import { SustainabilityFitMetricConfiguration } from './sustainability-fit-metric-configuration';
import { SustainabilityFocusConfiguration } from './sustainability-focus-configuration';
import { TargetingConfiguration } from './targeting-configuration';
import { TopBuyersSellersConfiguration } from './top-buyers-sellers.configuration';
import { TopHoldersConfiguration } from './top-holders-configuration';
import { TopShareholdersConfiguration } from './top-share-holders-configuration';
import { TradingActivityConfiguration } from './trading-activity-configuration';
import { YTDPriceVolumeConfiguration } from './ytd-price-volume-configuration';

export const reportSectionConfigurationTypeMap: Record<string, new () => ReportSectionConfigurationBase> = {
  ActivitiesConfiguration: ActivitiesConfiguration,
  ActivityHistoryConfiguration: ActivityHistoryConfiguration,
  AiSummaryCardConfiguration: AiSummaryCardConfiguration,
  ActivityIndicatorsConfiguration: ActivityIndicatorsConfiguration,
  CombinedOwnershipConfiguration: CombinedOwnershipConfiguration,
  CommentaryConfiguration: CommentaryConfiguration,
  ContactBiographyConfiguration: ContactBiographyConfiguration,
  ContactHeaderConfiguration: ContactHeaderConfiguration,
  CustomFieldsConfiguration: CustomFieldsConfiguration,
  BnyManagementReportHeaderConfiguration: BnyManagementReportHeaderConfiguration,
  TradingActivityConfiguration: TradingActivityConfiguration,
  PriceActivityConfiguration: PriceActivityConfiguration,
  TopHoldersConfiguration: TopHoldersConfiguration,
  DepositaryReceiptsContactConfiguration: DepositaryReceiptsContactConfiguration,
  EquityPortfolioConfiguration: EquityPortfolioConfiguration,
  FundsSummaryConfiguration: FundsSummaryConfiguration,
  HistoricalOwnershipConfiguration: HistoricalOwnershipConfiguration,
  InstitutionDetailsConfiguration: InstitutionDetailsConfiguration,
  InstitutionHeaderConfiguration: InstitutionHeaderConfiguration,
  InvestmentApproachConfiguration: InvestmentApproachConfiguration,
  MonthlyDrActivityConfiguration: MonthlyDrActivityConfiguration,
  NotesConfiguration: NotesConfiguration,
  OwnershipConfiguration: OwnershipConfiguration,
  PeerAndIndustryOwnershipConfiguration: PeerAndIndustryOwnershipConfiguration,
  SustainabilityFitMetricConfiguration: SustainabilityFitMetricConfiguration,
  SustainabilityFocusConfiguration: SustainabilityFocusConfiguration,
  TargetingConfiguration: TargetingConfiguration,
  CrossBorderTradingConfiguration: CrossBorderTradingConfiguration,
  DRIssueAndCancelActivityConfiguration: DRIssueAndCancelActivityConfiguration,
  HighTradingVolumeBrokersConfiguration: HighTradingVolumeBrokersConfiguration,
  DRPriceVolumeConfiguration: DRPriceVolumeConfiguration,
  YTDPriceVolumeConfiguration: YTDPriceVolumeConfiguration,
  TopBuyersSellersConfiguration: TopBuyersSellersConfiguration,
  WeekPriceVolumeConfiguration: WeekPriceVolumeConfiguration,
  CrossBrokerBorderConfiguration: CrossBorderBrokerConfiguration,
  ItineraryCommentsConfiguration: ItineraryCommentsConfiguration,
  ItineraryActivityHeaderConfiguration: ItineraryActivityHeaderConfiguration,
  ItineraryPeerOwnershipConfiguration: ItineraryPeerOwnershipConfiguration,
  ItineraryActivityHistoryConfiguration: ItineraryActivityHistoryConfiguration,
  ItineraryParticipantBioConfiguration: ItineraryParticipantBioConfiguration,
  ItineraryParticipantInfoConfiguration: ItineraryParticipantInfoConfiguration,
  ItineraryActivityAttributesConfiguration: ItineraryActivityAttributesConfiguration,
  TopShareholdersConfiguration: TopShareholdersConfiguration
};

@Injectable()
export class ReportTypeReportSections {
  private readonly commonSections: ReportSectionConfigurationWithOrder[] = [
    new ReportSectionConfigurationWithOrderImpl(1, false, CommentaryConfiguration),
    new ReportSectionConfigurationWithOrderImpl(2, true, InstitutionDetailsConfiguration),
    new ReportSectionConfigurationWithOrderImpl(3, true, InvestmentApproachConfiguration),
    new ReportSectionConfigurationWithOrderImpl(4, true, ContactBiographyConfiguration),
    new ReportSectionConfigurationWithOrderImpl(5, true, CustomFieldsConfiguration),
    new ReportSectionConfigurationWithOrderImpl(6, true, FundsSummaryConfiguration),
    new ReportSectionConfigurationWithOrderImpl(7, true, TargetingConfiguration),
    new ReportSectionConfigurationWithOrderImpl(8, true, OwnershipConfiguration),
    new ReportSectionConfigurationWithOrderImpl(9, true, ActivitiesConfiguration),
    new ReportSectionConfigurationWithOrderImpl(10, true, CombinedOwnershipConfiguration),
    new ReportSectionConfigurationWithOrderImpl(11, true, EquityPortfolioConfiguration),
    new ReportSectionConfigurationWithOrderImpl(12, true, PeerAndIndustryOwnershipConfiguration),
    new ReportSectionConfigurationWithOrderImpl(13, true, HistoricalOwnershipConfiguration),
    new ReportSectionConfigurationWithOrderImpl(14, true, ActivityHistoryConfiguration),
    new ReportSectionConfigurationWithOrderImpl(15, true, SustainabilityFocusConfiguration),
    new ReportSectionConfigurationWithOrderImpl(16, true, SustainabilityFitMetricConfiguration),
    new ReportSectionConfigurationWithOrderImpl(17, true, NotesConfiguration)
  ];

  private reportTypeMapping: Record<ReportType, ReportSectionConfigurationWithOrder[]> = {
    [ReportType.Institution]: [
      new ReportSectionConfigurationWithOrderImpl(0, true, InstitutionHeaderConfiguration),
      ...this.commonSections,
      new ReportSectionConfigurationWithOrderImpl(18, true, AiSummaryCardConfiguration)
    ],
    [ReportType.Contact]: [
      new ReportSectionConfigurationWithOrderImpl(0, true, ContactHeaderConfiguration),
      ...this.commonSections
    ],
    [ReportType.BnyManagementReport]: [
      new ReportSectionConfigurationWithOrderImpl(0, true, BnyManagementReportHeaderConfiguration),
      new ReportSectionConfigurationWithOrderImpl(1, true, DRIssueAndCancelActivityConfiguration),
      new ReportSectionConfigurationWithOrderImpl(2, true, TradingActivityConfiguration),
      new ReportSectionConfigurationWithOrderImpl(3, true, MonthlyDrActivityConfiguration),
      new ReportSectionConfigurationWithOrderImpl(4, true, DRPriceVolumeConfiguration),
      new ReportSectionConfigurationWithOrderImpl(5, true, PriceActivityConfiguration),
      new ReportSectionConfigurationWithOrderImpl(6, true, ActivityIndicatorsConfiguration),
      new ReportSectionConfigurationWithOrderImpl(7, true, WeekPriceVolumeConfiguration),
      new ReportSectionConfigurationWithOrderImpl(8, true, YTDPriceVolumeConfiguration),
      new ReportSectionConfigurationWithOrderImpl(9, true, CrossBorderTradingConfiguration),
      new ReportSectionConfigurationWithOrderImpl(10, true, HighTradingVolumeBrokersConfiguration),
      new ReportSectionConfigurationWithOrderImpl(11, true, CrossBorderBrokerConfiguration),
      new ReportSectionConfigurationWithOrderImpl(12, true, TopHoldersConfiguration),
      new ReportSectionConfigurationWithOrderImpl(13, true, TopBuyersSellersConfiguration),
      new ReportSectionConfigurationWithOrderImpl(14, true, DepositaryReceiptsContactConfiguration),
      new ReportSectionConfigurationWithOrderImpl(15, true, DisclosureConfiguration)
    ],
    [ReportType.CapitalInsight]: [],
    [ReportType.HistoricalOwnership]: [],
    [ReportType.Itinerary]: [
      new ReportSectionConfigurationWithOrderImpl(0, false, ItineraryCommentsConfiguration),
      new ReportSectionConfigurationWithOrderImpl(1, true, ItineraryActivityHeaderConfiguration),
      new ReportSectionConfigurationWithOrderImpl(2, true, ItineraryActivityAttributesConfiguration),
      new ReportSectionConfigurationWithOrderImpl(3, true, ItineraryParticipantInfoConfiguration)
    ],
    [ReportType.InstitutionTemplate]: [],
    [ReportType.ContactTemplate]: [],
    [ReportType.ItineraryTemplate]: [],
    [ReportType.OwnershipAnalyticsReport]: [
      new ReportSectionConfigurationWithOrderImpl(0, true, TopShareholdersConfiguration)
    ],
    [ReportType.None]: []
  };

  public static getReportSectionConfigurations(reportType: ReportType): ReportSectionConfigurationWithOrder[] {
    return new ReportTypeReportSections().getReportSections(reportType);
  }

  getReportSections(reportType: ReportType): ReportSectionConfigurationWithOrder[] {
    return [...this.reportTypeMapping[reportType]].map(section => {
      section.config.appliedReportType = reportType;

      if (reportType === ReportType.Institution && section.config.reportSection === ReportSection.InstitutionDetails) {
        section.config.availableDataSections = section.config.availableDataSections.slice(1);
      }
      // Handle nestedItems for each section
      if (Array.isArray(section.config.nestedItems)) {
        section.config.nestedItems = section.config.nestedItems.map((nestedItem: ReportSectionNestedDataSection) => {
          if (nestedItem.nestedItemSectionConfiguration && nestedItem.nestedItemSectionConfiguration.config) {
            nestedItem.nestedItemSectionConfiguration.config.appliedReportType = reportType;
          }
          return nestedItem;
        });
      }
      return section;
    });
  }

  static deserialize(data: string): ReportSectionConfigurationWithOrderImpl[] {
    const reportSections = JSON.parse(data) as {
      order: number;
      isSelected: boolean;
      configClass: string;
      config: string;
      width: number;
      appliedReportType: string;
    }[];
    return reportSections.map(section => {
      const configClass = reportSectionConfigurationTypeMap[section.configClass];
      const reportSectionConfig = new ReportSectionConfigurationWithOrderImpl(
        section.order,
        section.isSelected,
        configClass
      );
      switch (section.configClass) {
        case 'CommentaryConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as CommentaryConfiguration;
          break;
        case 'InstitutionDetailsConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as InstitutionDetailsConfiguration;
          break;
        case 'InvestmentApproachConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as InvestmentApproachConfiguration;
          break;
        case 'ContactBiographyConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ContactBiographyConfiguration;
          break;
        case 'CustomFieldsConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as CustomFieldsConfiguration;
          break;
        case 'FundsSummaryConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as FundsSummaryConfiguration;
          break;
        case 'TargetingConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as TargetingConfiguration;
          break;
        case 'OwnershipConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as OwnershipConfiguration;
          break;
        case 'ActivitiesConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ActivitiesConfiguration;
          break;
        case 'CombinedOwnershipConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as CombinedOwnershipConfiguration;
          break;
        case 'EquityPortfolioConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as EquityPortfolioConfiguration;
          break;
        case 'PeerAndIndustryOwnershipConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as PeerAndIndustryOwnershipConfiguration;
          break;
        case 'HistoricalOwnershipConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as HistoricalOwnershipConfiguration;
          break;
        case 'ActivityHistoryConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ActivityHistoryConfiguration;
          break;
        case 'SustainabilityFocusConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as SustainabilityFocusConfiguration;
          break;
        case 'SustainabilityFitMetricConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as SustainabilityFitMetricConfiguration;
          break;
        case 'AiSummaryCardConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as AiSummaryCardConfiguration;
          break;
        case 'NotesConfiguration': {
          const notesConfig = new NotesConfiguration();
          const parsedNotesConfig = JSON.parse(section.config);
          // Preserve the class's default additionalConfiguration and merge with parsed values
          const mergedAdditionalConfig = {
            ...notesConfig.additionalConfiguration,
            ...parsedNotesConfig.additionalConfiguration
          };
          // Copy only assignable properties from base class
          if (parsedNotesConfig.appliedReportType !== undefined) {
            notesConfig.appliedReportType = parsedNotesConfig.appliedReportType;
          }
          if (parsedNotesConfig.canSelectAllDataSections !== undefined) {
            notesConfig.canSelectAllDataSections = parsedNotesConfig.canSelectAllDataSections;
          }
          if (parsedNotesConfig.maxGridUnitsAllowed !== undefined) {
            notesConfig.maxGridUnitsAllowed = parsedNotesConfig.maxGridUnitsAllowed;
          }
          if (parsedNotesConfig.groupName !== undefined) {
            notesConfig.groupName = parsedNotesConfig.groupName;
          }
          if (parsedNotesConfig.nestedItems !== undefined) {
            notesConfig.nestedItems = parsedNotesConfig.nestedItems;
          }
          if (parsedNotesConfig.totalDataSectionsCount !== undefined) {
            notesConfig.totalDataSectionsCount = parsedNotesConfig.totalDataSectionsCount;
          }
          // Ensure additionalConfiguration includes our class defaults
          notesConfig.additionalConfiguration = mergedAdditionalConfig;
          reportSectionConfig.config = notesConfig;
          break;
        }
        case 'MonthlyDrActivityConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as MonthlyDrActivityConfiguration;
          break;
        case 'TradingActivityConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as TradingActivityConfiguration;
          break;
        case 'HighTradingVolumeBrokersConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as HighTradingVolumeBrokersConfiguration;
          break;
        case 'ActivityIndicatorsConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ActivityIndicatorsConfiguration;
          break;
        case 'DRIssueAndCancelActivityConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as DRIssueAndCancelActivityConfiguration;
          break;
        case 'CrossBorderTradingConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as CrossBorderTradingConfiguration;
          break;
        case 'PriceActivityConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as PriceActivityConfiguration;
          break;
        case 'DRPriceVolumeConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as DRPriceVolumeConfiguration;
          break;
        case 'YTDPriceVolumeConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as YTDPriceVolumeConfiguration;
          break;
        case 'WeekPriceVolumeConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as WeekPriceVolumeConfiguration;
          break;
        case 'CrossBorderBrokerConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as CrossBorderBrokerConfiguration;
          break;
        case 'TopHoldersConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as TopHoldersConfiguration;
          break;
        case 'TopBuyersSellersConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as TopBuyersSellersConfiguration;
          break;
        case 'DepositaryReceiptsContactConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as DepositaryReceiptsContactConfiguration;
          break;
        case 'ItineraryCommentsConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ItineraryCommentsConfiguration;
          break;
        case 'ItineraryActivityHeaderConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ItineraryActivityHeaderConfiguration;
          break;
        case 'ItineraryParticipantInfoConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ItineraryParticipantInfoConfiguration;
          break;
        case 'ItineraryActivityAttributesConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ItineraryActivityAttributesConfiguration;
          break;
        case 'ItineraryParticipantBioConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ItineraryParticipantBioConfiguration;
          break;
        case 'ItineraryPeerOwnershipConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as ItineraryPeerOwnershipConfiguration;
          break;
        case 'TopShareholdersConfiguration':
          reportSectionConfig.config = JSON.parse(section.config) as TopShareholdersConfiguration;
          break;
      }
      reportSectionConfig.config.appliedReportType = section.appliedReportType as ReportType;
      reportSectionConfig.width = section.width;
      return reportSectionConfig;
    });
  }
}

export function convertReportTemplateToParsedSections(
  reportTemplate: ReportTemplate,
  reportSections: ReportTypeReportSections
) {
  const settings = JSON.parse(reportTemplate.settings);
  let sections = settings.configurations;
  const isNewTemplate = !sections;

  if (!sections) {
    sections = JSON.parse(
      serializeReportSections(
        reportSections.getReportSections(
          reportTemplate.reportType
            ? ReportType[reportTemplate.reportType as keyof typeof ReportType]
            : ReportType.Institution
        )
      )
    );
    sections = sections.map((section: { config: string }) => {
      section.config = JSON.parse(section.config);
      return section;
    });
  } else {
    // Parse config if it's a string (for existing templates)
    sections = sections.map((section: { config: string | object }) => {
      if (typeof section.config === 'string') {
        section.config = JSON.parse(section.config);
      }
      return section;
    });
  }

  // For new itinerary templates, set comments section to unchecked by default
  if (isNewTemplate && reportTemplate.reportType === 'Itinerary') {
    sections = sections.map((section: ReportSectionConfigurationWithOrder) => {
      if (section.config?.name === 'enableItineraryCommentary') {
        return {
          ...section,
          isSelected: false
        };
      }
      return section;
    });
  }

  return { sections: sections as ReportSectionConfigurationWithOrder[], pageSize: settings.pageSize as PageSize };
}
